import MoneyInput from '../../Inputs/MoneyInput'
import Select from '../../Inputs/Select'
import CheckBox from 'components/CheckBox'
import {
  FEE_ROWS,
  PIX_ROW,
  NO_MACHINE_OPTION,
  NO_MACHINE_VALUE,
  UNREGISTERED_MACHINE,
  brandLabel,
  calcMachineFee,
  defaultMachineOption,
  findFeeRow,
  formatPercentage,
  getFee,
  grossUpValue,
  normalizeFees,
  pixFeeOf
} from 'services/CardMachine'

import {
  Form,
  FieldGrid,
  FormField,
  Input,
  Row,
  WarningText
} from './styles'

export default function PaymentForm ({
  index,
  formikProps,
  edittingEnabled = true,
  paymentMethods = [],
  bankAccounts = [],
  cardMachines = [],
  billValue
}) {
  const {
    values, touched, errors, handleChange, setFieldValue, setFieldTouched, handleBlur
  } = formikProps
  const paymentMethodsWithDescription = ['voucher', 'courtesy', 'exchange']
  const paymentMethodsWithBankAccount = ['pix', 'transfer', 'billet']
  const cardPaymentMethods = ['credit_card', 'debit_card']
  const managedPaymentMethods = ['used_credit']

  // "Dívida" não é um pagamento: é a declaração de que a comanda fecha sem o valor
  // inteiro pago. Por isso vale uma linha só, e o valor dela é o que faltou pagar,
  // calculado pelo formulário — digitar um número diferente da conta do servidor
  // era o que deixava a dívida do cliente divergindo da comanda.
  const debtPaymentIndex = values.payments.findIndex (payment => (
    payment?.paymentMethod?.value.codename === 'debt'
  ))
  const isDebtPayment = debtPaymentIndex === index

  const payment = values.payments[index]
  const codename = payment?.paymentMethod?.value.codename
  const isCredit = codename === 'credit_card'
  const isPix = codename === 'pix'
  const isCard = cardPaymentMethods.includes (codename)
  // O Pix também passa por maquininha, ou "sem maquininha", quando cai direto na conta bancária.
  const usesMachine = isCard || isPix
  const hasNoMachine = isPix && payment.cardMachine?.value === NO_MACHINE_VALUE
  // `value: null` é a "Maquininha sem registro", que não existe na API.
  const machineOf = cardMachineOption => (
    !cardMachineOption || cardMachineOption.value === NO_MACHINE_VALUE
      ? null
      : cardMachineOption.value === null
        ? UNREGISTERED_MACHINE
        : cardMachines.find(machine => machine.id === cardMachineOption.value)
  )
  const selectedMachine = usesMachine ? machineOf(payment.cardMachine) : null
  const machineFees = selectedMachine
    ? normalizeFees(selectedMachine.fees, selectedMachine.brands)
    : {}

  const resetMachineFields = () => {
    // A taxa repassada engordou o valor: trocar de método/maquininha sem desfazer isso deixaria
    // o campo de valor com um número que não quer dizer mais nada.
    if (payment.feeChargedToCustomer && payment.netValue != null) {
      setFieldValue(`payments[${index}].value`, payment.netValue)
    }
    setFieldValue(`payments[${index}].cardMachine`, null)
    setFieldValue(`payments[${index}].brand`, null)
    setFieldValue(`payments[${index}].modality`, null)
    setFieldValue(`payments[${index}].feePercent`, null)
    setFieldValue(`payments[${index}].feeValue`, null)
    setFieldValue(`payments[${index}].netValue`, null)
    setFieldValue(`payments[${index}].feeChargedToCustomer`, false)
    setFieldValue(`payments[${index}].installments`, null)
    setFieldValue(`payments[${index}].installmentValue`, null)
  }

  const handlePaymentMethodSelected = (name, option) => {
    // Trocar de método (inclusive entre crédito e débito) recomeça a escolha da maquininha.
    resetMachineFields ()

    // Cartão e Pix já vêm com a maquininha padrão do salão; o usuário só troca se for outra.
    const defaultMachine = defaultMachineOption(cardMachines)
    if (defaultMachine && [...cardPaymentMethods, 'pix'].includes(option?.value.codename)) {
      setFieldValue(`payments[${index}].cardMachine`, defaultMachine)
      setFieldValue(`payments[${index}].bankAccount`, null)

      // Pix não tem bandeira: a modalidade (e a taxa) já ficam prontas com a maquininha padrão.
      if (option.value.codename === 'pix') {
        const modality = { label: PIX_ROW.label, value: PIX_ROW.id }
        setFieldValue(`payments[${index}].modality`, modality)
        // `feeChargedToCustomer: false` explícito: `payment` ainda é o retrato de antes do
        // `resetMachineFields` (Formik só aplica no próximo render), então sem isso o repasse
        // de um método anterior vazaria para este Pix.
        updateMachineFee({ cardMachine: defaultMachine, modality, feeChargedToCustomer: false })
      }
    }

    if (!paymentMethodsWithBankAccount.includes (option?.value.codename))
      setFieldValue (`payments[${index}].bankAccount`, undefined)
    if (!paymentMethodsWithDescription.includes (option?.value.codename))
      setFieldValue (`payments[${index}].description`, '')

    if (index === 0 && values.payments.length === 1)
      setFieldValue (`payments[${index}].value`, billValue)
    setFieldValue (name, option)
  }

  // Recalcula o que depende de modalidade/bandeira/valor/repasse na linha de cartão. `overrides`
  // leva o campo que acabou de mudar, que ainda não chegou em `values`.
  const updateMachineFee = (overrides = {}) => {
    const next = { ...payment, ...overrides }
    const machine = machineOf(next.cardMachine)
    const fees = machine ? normalizeFees(machine.fees, machine.brands) : {}
    const row = findFeeRow(next.modality?.value)
    const brand = next.brand?.value
    // Pix tem uma taxa só na maquininha, sem bandeira; as demais modalidades são por bandeira.
    const fee = !row
      ? null
      : row.id === PIX_ROW.id
        ? (machine ? pixFeeOf(machine.fees, machine.brands) : null)
        : (brand && fees[brand] ? getFee(fees, brand, row) : null)

    /*
      Com a taxa repassada ao cliente, `value` é o valor engordado (o que vai na maquininha), não
      o preço do serviço — então uma mudança que não seja o valor digitado direto (ligar o
      repasse, ou trocar maquininha/bandeira/modalidade já com ele ligado) precisa regordar a
      partir da mesma base, e não a partir do valor antigo, que já tinha a taxa anterior embutida.
      Desligar o repasse volta para a base (o líquido já gravado). Editar o valor direto, com o
      repasse ligado ou não, é sempre o valor final — sem regordar nada.
    */
    const valueEdited = 'value' in overrides
    const turningOn = next.feeChargedToCustomer && !payment.feeChargedToCustomer
    const turningOff = !next.feeChargedToCustomer && payment.feeChargedToCustomer

    let value = next.value
    if (!valueEdited) {
      if (turningOn) value = grossUpValue(payment.value, fee)
      else if (turningOff) value = Number(payment.netValue ?? payment.value) || 0
      else if (next.feeChargedToCustomer) value = grossUpValue(payment.netValue ?? payment.value, fee)
    }

    if (value !== next.value) setFieldValue(`payments[${index}].value`, value)

    const { feeValue, netValue } = calcMachineFee(value, fee)
    const installments = row?.field === 'credit' ? row.index + 1 : null

    setFieldValue(`payments[${index}].feePercent`, fee)
    setFieldValue(`payments[${index}].feeValue`, row ? feeValue : null)
    setFieldValue(`payments[${index}].netValue`, row ? netValue : null)
    setFieldValue(`payments[${index}].installments`, installments)
    setFieldValue(`payments[${index}].installmentValue`, installments ? value / installments : null)
  }

  const handleFeeChargedToCustomerChanged = value => {
    setFieldValue(`payments[${index}].feeChargedToCustomer`, value)
    updateMachineFee({ feeChargedToCustomer: value })
  }

  const handleMachineSelected = (name, option) => {
    resetMachineFields()
    // Com maquininha o dinheiro cai na conta dela, não na do salão: a conta bancária só vale sem ela.
    if (option?.value !== NO_MACHINE_VALUE) setFieldValue(`payments[${index}].bankAccount`, null)
    setFieldValue(name, option)

    // Pix não tem bandeira: a modalidade (e a taxa) já ficam prontas ao escolher a maquininha.
    if (isPix && option?.value !== NO_MACHINE_VALUE) {
      const modality = { label: PIX_ROW.label, value: PIX_ROW.id }
      setFieldValue(`payments[${index}].modality`, modality)
      // Mesmo motivo do `feeChargedToCustomer: false` em `handlePaymentMethodSelected`.
      updateMachineFee({ cardMachine: option, modality, feeChargedToCustomer: false })
    }
  }

  // Débito só tem uma modalidade, então ela fica escolhida sozinha junto com a bandeira.
  const handleBrandSelected = (name, option) => {
    const modality = isCredit ? payment.modality : { label: 'Débito', value: 'debit' }

    setFieldValue(name, option)
    setFieldValue(`payments[${index}].modality`, modality)
    updateMachineFee({ brand: option, modality })
  }

  const fieldError = field => touched.payments?.[index]?.[field] && errors.payments?.[index]?.[field]
  const machineOptions = [
    ...isPix ? [NO_MACHINE_OPTION] : [],
    ...[UNREGISTERED_MACHINE, ...cardMachines].map(machine => ({ label: machine.name, value: machine.id }))
  ]
  const brandOptions = (selectedMachine?.brands ?? []).map(brand => ({ label: brandLabel(brand), value: brand }))
  // O valor da parcela e a taxa vêm ao lado de cada modalidade para o usuário escolher o número
  // de parcelas já vendo quanto paga por mês e quanto isso custa. Sem valor digitado, só a taxa.
  const paymentValue = Number(payment?.value) || 0
  const modalityOptions = FEE_ROWS.filter(row => row.field === 'credit').map(row => {
    const fee = machineFees[payment?.brand?.value] ? getFee(machineFees, payment.brand.value, row) : null
    const feeText = fee === null || fee === undefined ? 'sem taxa informada' : `Taxa de ${formatPercentage(fee)}`
    const installmentText = paymentValue > 0
      ? ` de ${(paymentValue / (row.index + 1)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
      : ''

    return { label: `${row.label}${installmentText} - ${feeText}`, value: row.id }
  })

  return (
    <Form>
      <FieldGrid index={index} values={values}>
        <FormField>
          {touched.payments?.[index]?.paymentMethod && errors.payments?.[index]?.paymentMethod && (
            <WarningText>{errors.payments[index].paymentMethod}</WarningText>
          )}
          <Select
            name={`payments[${index}].paymentMethod`}
            placeholder='Método de pagamento...'
            value={values.payments[index]?.paymentMethod ? {
              label: values.payments[index]?.paymentMethod.label,
              value: values.payments[index]?.paymentMethod.value
            }: null}
            options={paymentMethods.filter (paymentMethod => (
              !managedPaymentMethods.includes (paymentMethod.codename)
              && (paymentMethod.codename !== 'debt' || debtPaymentIndex < 0 || isDebtPayment)
            )).map (paymentMethod => ({
              label: paymentMethod.name,
              value: paymentMethod
            }))}
            isClearable={true}
            isDisabled={!edittingEnabled}
            onChange={handlePaymentMethodSelected}
            onBlur={setFieldTouched}
          />
        </FormField>
        {usesMachine && (
          <>
            <FormField>
              {fieldError('cardMachine') && <WarningText>{errors.payments[index].cardMachine}</WarningText>}
              <Select
                name={`payments[${index}].cardMachine`}
                placeholder='Maquininha...'
                value={payment.cardMachine ?? null}
                options={machineOptions}
                isDisabled={!edittingEnabled}
                onChange={handleMachineSelected}
                onBlur={setFieldTouched}
              />
            </FormField>
            {!hasNoMachine && !isPix && (
              <FormField>
                {fieldError('brand') && <WarningText>{errors.payments[index].brand}</WarningText>}
                <Select
                  name={`payments[${index}].brand`}
                  placeholder='Bandeira...'
                  value={payment.brand ?? null}
                  options={brandOptions}
                  isDisabled={!edittingEnabled || !selectedMachine}
                  onChange={handleBrandSelected}
                  onBlur={setFieldTouched}
                />
              </FormField>
            )}
            {isCredit && (
              <FormField>
                {fieldError('modality') && <WarningText>{errors.payments[index].modality}</WarningText>}
                <Select
                  name={`payments[${index}].modality`}
                  placeholder='Modalidade...'
                  value={modalityOptions.find(option => option.value === payment.modality?.value) ?? payment.modality ?? null}
                  options={modalityOptions}
                  isDisabled={!edittingEnabled || !payment.brand}
                  onChange={(name, option) => {
                    setFieldValue(name, option)
                    updateMachineFee({ modality: option })
                  }}
                  onBlur={setFieldTouched}
                />
              </FormField>
            )}
          </>
        )}
        {paymentMethodsWithDescription.includes (
          values.payments[index]?.paymentMethod?.value.codename
        ) && (
          <Input
            type='text'
            placeholder='Descrição'
            value={values.payments[index]?.description}
            onChange={handleChange (`payments[${index}].description`)}
            onBlur={handleBlur (`payments[${index}].description`)}
            disabled={!edittingEnabled}
          />
        )}
        {paymentMethodsWithBankAccount.includes (
          values.payments[index]?.paymentMethod?.value.codename
        ) && (!isPix || hasNoMachine) && (
          <Select
            name={`payments[${index}].bankAccount`}
            placeholder='Conta bancária...'
            value={values.payments[index]?.bankAccount ? {
              label: values.payments[index]?.bankAccount.label,
              value: values.payments[index]?.bankAccount.value
            }: null}
            options={
              bankAccounts.map (bankAccount => ({
                label: `(${bankAccount.bank.code}) ${bankAccount.bank.name} - ${bankAccount.description}`,
                value: bankAccount
              }))
            }
            isDisabled={!edittingEnabled}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
          />
        )}
        <FormField>
          <MoneyInput
            name={`payments[${index}].value`}
            placeholder='Valor'
            value={values.payments[index]?.value}
            onValueChange={(name, value) => {
              setFieldValue (name, value)
              if (isCard || selectedMachine) updateMachineFee ({ value })
              else setFieldValue (`payments[${index}].installmentValue`, value)
            }}
            onBlur={handleBlur (`services[${index}].value`)}
            disabled={!edittingEnabled || isDebtPayment}
          />
        </FormField>
      </FieldGrid>
      {usesMachine && !hasNoMachine && (
        <Row style={{justifyContent: 'flex-start', alignItems: 'center', marginTop: 5}}>
          <CheckBox
            id={`feeChargedToCustomer-${index}`}
            style={{marginRight: -3}}
            value={!!payment.feeChargedToCustomer}
            onValueChange={handleFeeChargedToCustomerChanged}
            disabled={!edittingEnabled}
          />
          <label htmlFor={`feeChargedToCustomer-${index}`} style={{cursor: 'pointer'}}>
            Repassar taxa da maquininha ao cliente
          </label>
        </Row>
      )}
    </Form>
  )
}
